/* eslint-disable arrow-parens */
import * as React from 'react';
import { RegExpErrorsText } from '_helpers';
import { IApiError } from '_helpers/ApiConnector/types';

interface Rule {
    regexp?: RegExp;
    errorText: string;
    required?: boolean;
}


type RulesType<F> = Record<keyof F, Rule>;
type FormErrorsType<F> = Partial<Record<keyof F, string>>

const useForm = <T>(
  initialForm: T,
  rules: Partial<RulesType<T>>,
): [
    T,
    Partial<Record<keyof T, string>>,
    (field: keyof T, value: T[keyof T]) => void,
    (form: T) => void,
    (callback: (form: T) => void, ignoreFields?: (keyof RulesType<T>)[]) => void,
    (apiError: IApiError) => void,
] => {
    const [form, setForm] = React.useState<T>(initialForm);
    const [edited, setEdited] = React.useState<Partial<Record<keyof T, boolean>>>({});
    const [errors, setErrors] = React.useState<FormErrorsType<T>>({});

    const validateField = (field: keyof T, value: T[keyof T]): string | undefined => {
        const rule = rules[field];
        if (!rule) return undefined;

        if (rule.required && !value) {
            return RegExpErrorsText.REQUIRED;
        }

        if (!!value && rule.regexp && !rule.regexp.test(String(value))) {
            return rule.errorText;
        }

        return undefined;
    };

    const validateForm = (formForValidate: T, editedFields: Partial<Record<keyof T, boolean>>, withoutEdited?: boolean) => {
        const validateErrors: FormErrorsType<T> = { ...errors };
        Object.keys(rules).forEach((k) => {
            const key = k as keyof T;
            if (editedFields[key] || withoutEdited) {
                validateErrors[key] = validateField(key, formForValidate[key]);
            }
        });

        return validateErrors;
    };

    const handleChange = (field: keyof T, value: T[keyof T]) => {
        let newForm = { ...form };
        setForm((prev) => {
            newForm = {
                ...prev,
                [field]: value,
            };
            return newForm;
        });
        setEdited((prev) => {
            const newEdited = {
                ...prev,
                [field]: true,
            };
            setErrors(validateForm(newForm, newEdited));
            return newEdited;
        });
    };

    const handleSubmit = (callback: (form: T) => void, ignoreFields: (keyof RulesType<T>)[] = []) => {
        const validateErrors = validateForm(form, edited, true);
        ignoreFields.forEach(field => {
            validateErrors[field] = undefined;
        });
        const hasErrors = Object.values(validateErrors).filter(v => !!v).length > 0;
        setErrors(validateErrors);
        if (hasErrors) return;
        callback(form);
    };

    const setErrorsFromApi = (apiError: IApiError) => {
        const validateErrors: FormErrorsType<T> = {};

        const { failures } = apiError;

        if (!failures) return;


        Object.keys(failures).forEach((key) => {
            if (!key || !(key in form)) return;

            [validateErrors[key as keyof T]] = failures[key];
        });

        setErrors(prevState => ({ ...prevState, ...validateErrors }));
    };

    return [form, errors, handleChange, setForm, handleSubmit, setErrorsFromApi];
};

export default useForm;
